import { keepPreviousData, useQuery } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';
import http from '@/services/http';
import type { TrackMatchingCursor, TrackMatchingResponse } from '@/types/types';
import { filterNullValues } from '@/utils/filterUtils';
import type { IScope } from '@/types/filterBuilderTypes';
import { isGroup, isRule } from '@/types/typeguards/QueryBuilder';

const PARTY_ID_COLUMNS = ['match_sender_party_ids', 'match_sent_on_behalf_of_party_ids'] as const;

function ensurePartyIdsAreNumbers(scope: IScope | null | undefined): IScope | null {
  if (!scope) return null;
  if (isRule(scope)) {
    const ruleColumn = scope.column;
    const ruleValue = scope.value;
    if (PARTY_ID_COLUMNS.includes(ruleColumn as (typeof PARTY_ID_COLUMNS)[number]) && Array.isArray(ruleValue)) {
      const numericValues = ruleValue
        .map((rawValue: unknown) => (typeof rawValue === 'number' ? rawValue : Number(rawValue)))
        .filter((numericValue: number) => !isNaN(numericValue));
      return { ...scope, value: numericValues };
    }
    return scope;
  }
  if (isGroup(scope)) {
    return {
      ...scope,
      rules: scope.rules
        .map((childScope) => ensurePartyIdsAreNumbers(childScope))
        .filter((childScope): childScope is IScope => childScope !== null),
    };
  }
  return null;
}


export interface TrackMatchingApiParams {
  /** Cursor request: an opaque token (or 'last' sentinel / null) plus whether to count the total. */
  cursor: Ref<TrackMatchingCursor>;
  pageSize: Ref<number>;
  sort?: Ref<object>;
  filter?: Ref<object>;
}

export function useTrackMatchingApi({ cursor, pageSize, sort, filter }: TrackMatchingApiParams) {
  const queryKey = ['trackMatching', cursor, pageSize, sort, filter];
  const queryFn = () => {
    // Filter out rules with null values before sending to backend
    let cleanedFilter = undefined;
    if (filter?.value && typeof filter.value === 'object' && 'ruleScopes' in filter.value) {
      const rawScope = filter.value.ruleScopes as IScope;
      const scopeWithNumericPartyIds = ensurePartyIdsAreNumbers(rawScope);
      const cleanedScope = filterNullValues(scopeWithNumericPartyIds);
      if (cleanedScope) {
        cleanedFilter = { ruleScopes: cleanedScope };
      }
    }

    // Cursor (search_after) paging instead of an offset page number, so the list can be
    // navigated end-to-end (incl. Last) without hitting Elasticsearch's 10k result window. The
    // cursor is an opaque token echoed back from a prior response (or the 'last' sentinel).
    return http
        .post(`/api/live-track-match`, {
            services: {
                raw_ddex: true,
                spotify: true
            },
            perPage: pageSize?.value,
            cursor: cursor.value.token ?? undefined,
            last: cursor.value.last || undefined,
            withTotal: cursor.value.withTotal,
            sort: sort?.value || undefined,
            filter: cleanedFilter
        }).then((apiResponse) => apiResponse.data as TrackMatchingResponse);
  };

  const query = useQuery({
    queryKey,
    queryFn,
    retry: 0,
    // Keep the previous page's response (incl. `total`) visible while the next page
    // fetches. Without this, vue-query clears `data` on every page change, so
    // `data.total` momentarily drops to undefined and the "X / Y" pager flips its
    // total to 1 mid-paging. Matches the other paginated tables (useAudioFileIssuesApi,
    // usePlaylistSwitchingApi) and the isFetching-based loading state below.
    placeholderData: keepPreviousData,
  });
  
  // Use isFetching instead of isLoading to show loading state during refetches (e.g., when sorting)
  // isLoading only returns true on initial load when there's no data
  // isFetching returns true whenever a fetch is in progress, including refetches
  return {
    ...query,
    isLoading: computed(() => query.isFetching.value || query.isLoading.value),
    /** Bumps when a fetch settles; use to resync row state even if `data` keeps the same object reference. */
    dataUpdatedAt: computed(() => query.dataUpdatedAt.value),
  };
}
